/**
 * Footer newsletter form (Deployteq) with Google reCAPTCHA v3.
 *
 * Markup: templates/_partials/globals/footer.twig  (`form[data-newsletter-form]`)
 * Backend: POST /actions/newsletter/subscribe  (modules/Newsletter)
 *
 * - api.js (and its badge/cookies) is only loaded once the visitor starts
 *   typing in the form, not on page load / focus / hover. Submit loads it as a
 *   fallback (e.g. browser autofill that doesn't fire `input`).
 * - A fresh token is requested at submit time (tokens expire after 2 minutes).
 * - The form is submitted via fetch() and the result is shown inline, because
 *   the page itself is full-page cached.
 */

import { getToken, loadRecaptcha } from './recaptcha.js';

const GENERIC_ERROR = 'Er ging iets mis bij het aanmelden. Probeer het later opnieuw.';
const RECAPTCHA_ERROR = 'De beveiligingscontrole is mislukt. Controleer je internetverbinding en probeer het opnieuw.';

const initForm = (form) => {
  const siteKey = form.dataset.recaptchaSiteKey || '';
  const action = form.dataset.recaptchaAction || 'newsletter_subscribe';
  const message = form.querySelector('[data-newsletter-message]');
  const buttons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
  let busy = false;

  const setBusy = (state) => {
    busy = state;
    buttons.forEach((button) => {
      button.disabled = state;
      button.setAttribute('aria-busy', state ? 'true' : 'false');
    });
  };

  const showMessage = (text, isError) => {
    if (!message) return;
    message.textContent = text;
    message.setAttribute('role', isError ? 'alert' : 'status');
    message.classList.toggle('font-bold', isError);
    message.classList.remove('hidden');
  };

  if (siteKey) {
    // Activate reCAPTCHA only once the visitor actually starts typing, so it's
    // warmed up (and has gathered some signal) by the time they submit.
    form.addEventListener('input', () => loadRecaptcha(siteKey).catch(() => {}), { once: true });
  }

  // Native constraint validation (required/type=email) runs before this fires.
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    message?.classList.add('hidden');

    const body = new FormData(form);

    try {
      if (siteKey) {
        body.set('g-recaptcha-response', await getToken(siteKey, action));
      }
    } catch (e) {
      showMessage(RECAPTCHA_ERROR, true);
      setBusy(false);
      return;
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        form.reset();
        showMessage(data.message || 'Bedankt voor je aanmelding voor onze nieuwsbrief!', false);
      } else {
        showMessage(data.message || GENERIC_ERROR, true);
      }
    } catch (e) {
      showMessage(GENERIC_ERROR, true);
    } finally {
      setBusy(false);
    }
  });
};

export default function prepareNewsletterForm() {
  document.querySelectorAll('form[data-newsletter-form]').forEach(initForm);
}
